import { type ImportedDataState } from '@excalidraw/excalidraw/types/data/types';
import { type ExcalidrawElement, type NonDeletedExcalidrawElement } from '@excalidraw/excalidraw/types/element/types';
import {
    type AppState,
    type BinaryFileData,
    type ExcalidrawImperativeAPI,
    type ExcalidrawProps,
    type LibraryItem,
    type Zoom
} from '@excalidraw/excalidraw/types/types';
import { type ResolvablePromise } from '@excalidraw/excalidraw/types/utils';

import { type UserRoles } from '../roster/types';

export enum SessionTypes {
    Shared = 'shared',
    Individual = 'private',
    Presentation = 'presentation',
    Annotations = 'annotations',
    Breakout = 'breakout'
}

export enum PageTypes {
    Shared = 'shared',
    Individual = 'private',
    Pdf = 'pdf',
    Template = 'template',
    Breakout = 'breakout'
}

type DatabaseObject = {
    created_by: number;
    created_at: string;
    updated_at: string;
};

export type PageCustomData = {
    backgroundElement?: ExcalidrawElement;
    note?: string;
    breakout_name?: string;
    student_ids?: number[];
};

export type Page = DatabaseObject & {
    page_id: string;
    session_id: string;
    name: string;
    is_locked: boolean;
    order: number;
    write_access: UserRoles;
    type: PageTypes;
    data?: PageCustomData;
};

export type Session = DatabaseObject & {
    session_id: string;

    class_id: string;

    name: string;

    /**
     * Session type dictates page and permission behavior.
     */
    type: SessionTypes;

    /**
     * Which role can edit/draw on the whiteboard.
     */
    write_access: UserRoles;

    /**
     * Which role can lock or unlock pages.
     */
    page_lock_access: UserRoles;

    /**
     * Which role can manage pages.
     */
    page_write_access: UserRoles;

    /**
     * Which role can start presenting.
     */
    presentation_access: UserRoles;
    data?: any;
};

export type Element = DatabaseObject & {
    element_id: string;
    page_id: string;
    version: number;
    version_nonce: number;
    body: ExcalidrawElement;
};

export type UserInfo = {
    user_uuid: string;
    student_uuid?: string;
    student_id: number;
    userName: string;
    role: UserRoles;
    picUrl?: string;
    breakout_name?: string;
};

export type LibraryGroup = {
    name: string;
    libraryItems: LibraryItem[];
    files: Record<string, BinaryFileData>;
};

// Response shape from /v1/whiteboard/student-info
export type StudentInfo = {
    firstName: string;
    lastName: string;
    role: UserRoles;
    studentId?: number;
    studentUuid?: string;
};

export type Presenter = {
    student_id: number;
    userName: string;
    role: UserRoles;
    picUrl?: string;
    scrollX?: number;
    scrollY?: number;
    zoom?: Zoom;
    currentPageId?: string;
    offsetTop?: number;
    offsetLeft?: number;
    width?: number;
    height?: number;
    timestamp?: number;
    shareStudentPages?: boolean;
    pointer: Position;
};

export interface Position {
    x: number;
    y: number;
    tool?: 'pointer' | 'laser';
}

export enum InitializationStatus {
    NotStarted = 'NOT_STARTED',
    InProgress = 'IN_PROGRESS',
    Complete = 'COMPLETE'
}

export enum Modes {
    MODE_PRESENTING = 'presenting',
    MODE_FREENAV = 'freenav'
}

// --- The following types are from Excalidraw ---

export type Collaborator = {
    pointer: any;
    button?: 'up' | 'down';
    selectedElementIds?: AppState['selectedElementIds'];
    username?: string | null;
    userState?: UserIdleState;
    color?: {
        background: string;
        stroke: string;
    };
};

export type NormalizedZoomValue = number & { _brand: 'normalizedZoom' };

export type PointerCoords = Readonly<{
    x: number;
    y: number;
}>;

export type Gesture = {
    pointers: Map<number, PointerCoords>;
    lastCenter: { x: number; y: number } | null;
    initialDistance: number | null;
    initialScale: number | null;
};

export declare class GestureEvent extends UIEvent {
    readonly rotation: number;
    readonly scale: number;
}

// NOTE ready/readyPromise props are optional for host apps' sake (our own
// implem guarantees existence)
export type ExcalidrawAPIRefValue =
    | ExcalidrawImperativeAPI
    | {
          readyPromise?: ResolvablePromise<ExcalidrawImperativeAPI>;
          ready?: false;
      };

export type SceneData = {
    elements?: ImportedDataState['elements'];
    appState?: ImportedDataState['appState'];
    collaborators?: Map<string, Collaborator>;
    commitToHistory?: boolean;
};

export enum UserIdleState {
    ACTIVE = 'active',
    AWAY = 'away',
    IDLE = 'idle'
}

export type ExportOpts = {
    saveFileToDisk?: boolean;
    onExportToBackend?: (
        exportedElements: readonly NonDeletedExcalidrawElement[],
        appState: AppState,
        canvas: HTMLCanvasElement | null
    ) => void;
    renderCustomUI?: (
        exportedElements: readonly NonDeletedExcalidrawElement[],
        appState: AppState,
        canvas: HTMLCanvasElement | null
    ) => JSX.Element;
};

type CanvasActions = {
    changeViewBackgroundColor?: boolean;
    clearCanvas?: boolean;
    export?: false | ExportOpts;
    loadScene?: boolean;
    saveToActiveFile?: boolean;
    theme?: boolean;
    saveAsImage?: boolean;
};

export type UIOptions = {
    canvasActions?: CanvasActions;
};

export type AppProps = ExcalidrawProps & {
    UIOptions: {
        canvasActions: Required<CanvasActions> & { export: ExportOpts };
    };
    detectScroll: boolean;
    handleKeyboardGlobally: boolean;
};

export interface SessionOptions {
    allowPageNavigation: boolean;
    canEditPages: boolean;
    canLockPages: boolean;
    canPresent: boolean;
    canStopPresenting: boolean;
    displayEditingToggle: boolean;
    isPresentationViewer: boolean;
    presentationAccess: boolean;
    showPageList: boolean;
    studentEditingEnabled: boolean;
}

export interface InitialPageData {
    elements: ExcalidrawElement[];
    files: Record<ExcalidrawElement['id'], BinaryFileData>;
    session?: Session;
    appState?: Partial<AppState>;
}
